import React from 'react';
import { Box, Container, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';

const BANNER_KEY_PREFIX = 'kt.banner.redesign_2025';
const DISMISSED_AT_KEY = `${BANNER_KEY_PREFIX}.dismissedAt`;
const VIEWED_SESSION_KEY = `${BANNER_KEY_PREFIX}.viewed.session`;

// 30 days in ms
const SHOW_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
// Hard sunset date (stop showing after this date regardless)
const SUNSET = new Date('2026-02-28T00:00:00Z').getTime();

function now() {
  try { return Date.now(); } catch { return new Date().getTime(); }
}

function evaluateBannerVisibility() {
  const logPrefix = '[Banner]';
  const currentTime = now();
  let reason = 'default';


  if (currentTime > SUNSET) {
    reason = 'after_sunset';
    return { show: false, reason };
  }

  // Respect dismiss for 30 days; after that, show again until sunset
  const rawDismissed = localStorage.getItem(DISMISSED_AT_KEY);
  const dismissedAt = Number(rawDismissed || 0);
  if (dismissedAt > 0) {
    const age = currentTime - dismissedAt;
    if (age <= SHOW_WINDOW_MS) {
      reason = 'recently_dismissed';
      try { console.log(`${logPrefix} hidden due to recent dismiss`, { dismissedAt, dismissedAtIso: new Date(dismissedAt).toISOString(), ageMs: age }); } catch (_) {}
      return { show: false, reason };
    }
  }

  reason = 'visible_until_sunset';
  return { show: true, reason };
}

function getCopyForPath(pathname) {
  // Tailored copy per audience
  if (pathname.startsWith('/apply')) {
    return {
      title: 'New look, smoother application.',
      subtitle: 'Same commitment to safety, pay, and being home most weekends in selected areas.',
      cta: 'Learn what’s new',
      ctaTo: '/about/redesign-2025'
    };
  }
  if (pathname === '/' || pathname.startsWith('/fleet') || pathname.startsWith('/services')) {
    return {
      title: 'We’ve refreshed our look.',
      subtitle: 'Cleaner, faster experience — same trusted service since 2013.',
      cta: 'Learn what’s new',
      ctaTo: '/about/redesign-2025'
    };
  }
  return {
    title: 'Site refresh in progress.',
    subtitle: 'New design, same reliability.',
    cta: 'What changed',
    ctaTo: '/about/redesign-2025'
  };
}

const AnnouncementBanner = () => {
  const location = useLocation();
  const [visible, setVisible] = React.useState(false);
  const copy = React.useMemo(() => getCopyForPath(location.pathname), [location.pathname]);

  React.useEffect(() => {
    const { show, reason } = evaluateBannerVisibility();
    setVisible(show);
    if (show) {
      // Track only once per session
      const viewed = sessionStorage.getItem(VIEWED_SESSION_KEY);
      if (!viewed) {
        trackEvent('banner_view', { banner: 'redesign_2025', path: location.pathname, reason });
        try { sessionStorage.setItem(VIEWED_SESSION_KEY, '1'); } catch (_) {}
      }
    }
  }, [location.pathname]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_AT_KEY, String(now())); } catch (_) {}
    setVisible(false);
    try { console.log('[Banner] dismiss clicked'); } catch (_) {}
    trackEvent('banner_dismiss', { banner: 'redesign_2025', path: location.pathname });
  };

  if (!visible) return null;

  try { console.log('[Banner] render', { visible, path: location.pathname }); } catch (_) {}

  return (
    <Box role="region" aria-label="Site update announcement" sx={{ bgcolor: '#f6f8fb', borderBottom: '1px solid #e0e0e0' }} data-testid="announcement-banner">
      <Container maxWidth="lg" sx={{ py: 1.25, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {copy.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            {copy.subtitle}
          </Typography>
        </Box>

        <Button
          component={RouterLink}
          to={copy.ctaTo}
          size="small"
          variant="outlined"
          onClick={() => trackEvent('banner_learn_more_click', { banner: 'redesign_2025', path: location.pathname })}
          sx={{
            whiteSpace: 'nowrap'
          }}
        >
          {copy.cta}
        </Button>

        <IconButton aria-label="Dismiss announcement" onClick={dismiss} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Container>
    </Box>
  );
};

export default AnnouncementBanner;


